import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Divider, Select, TextInput } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'tabler-icons-react';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import PageView from '../../common/PageView';
import AppFlexbox from '../../common/AppFlexbox';
import {
  ECOM_PRODUCT_BUNDLE_TYPE_ENUM,
  ECOM_PRODUCT_PRICING_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import AppStack from '../../common/AppStack';
import EditProductCard from '../products/EditProductCard';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import VendorCollectionProductSelectFormSection from '../vendors/VendorCollectionProductSelectFormSection';
import BundleDiscountEditControls from './BundleDiscountEditControls';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import * as classes from '../../../styles/nestedStyles.module.css';
import { triggerNotification } from '../../../helpers/notification';

const initialFormState = {
  name: '',
  fkEcomProductStatus: ECOM_PRODUCT_STATUS_ENUM.DRAFT,
  ecomVendorBundleDiscounts: [
    {
      sort: 1,
      minQuantity: 1,
      fkEcomPricingType: ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT,
      pricingAmount: null
    }
  ],
  ecomVendorBundleProducts: []
};

const VolumeDiscountBundleEdit = ({
  backPath,
  onBackClick,
  duplicate,
  ecomVendorBundle
}) => {
  const navigate = useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { state, createVendorBundles } = useContext(VendorContext);
  const { isLaptopOrSmaller } = useMediaQueryIndex();
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(initialFormState, {
    confirmDiscard: true,
    containerWidth: 1100
  });

  useEffect(() => {
    resetFormState({ ...initialFormState });
  }, []);

  return (
    <PageView
      backPath={backPath}
      formProps={{
        withFormControls: true,
        hasUnsavedChanges,
        isSubmitting,
        ...(!!ecomVendorBundle && !duplicate
          ? {
              onDelete: () => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE, ecomVendorBundle);
              }
            }
          : {}),
        onSubmit: (e) => {
          e.preventDefault();
          e.stopPropagation();

          submitFormState((formData, onErrorCallback) => {
            createVendorBundles(
              authState.pkEcomVendor,
              {
                ecomVendorBundles: [
                  {
                    name: formState.name,
                    description: formState.description,
                    fkEcomProductStatus: formState.fkEcomProductStatus,
                    fkEcomBundleType:
                      ECOM_PRODUCT_BUNDLE_TYPE_ENUM.VOLUME_DISCOUNT,
                    ecomVendorBundleDiscounts:
                      formState.ecomVendorBundleDiscounts,
                    ecomVendorBundleProducts: formState.ecomVendorBundleProducts.map(
                      (p) => ({
                        pkEcomVendorBundleProduct: p.pkEcomVendorBundleProduct,
                        fkEcomProduct: p.fkEcomProduct
                      })
                    )
                  }
                ]
              },
              (data) => {
                navigate(
                  `/vendor/product-bundles/${data[0].pkEcomVendorBundle}`
                );
                triggerNotification('Bundle created!', 'success');
              },
              onErrorCallback
            );
          });
        }
      }}
      onBackClick={onBackClick}
      rightSection={
        !!ecomVendorBundle &&
        !duplicate && (
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <Button
              color="dark"
              component={Link}
              disabled={isSubmitting}
              radius="md"
              size="xs"
              to={`/vendor/product-bundles/${ecomVendorBundle.pkEcomVendorBundle}/duplicate`}
              type="button"
              variant="light"
            >
              Duplicate
            </Button>
          </AppFlexbox>
        )
      }
      title={ecomVendorBundle?.name ?? `Create volume discount`}
      width={1100}
    >
      <AppFlexbox
        style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
      >
        <AppStack
          style={{
            flex: 2,
            gap: 16
          }}
        >
          <VendorCollectionProductSelectFormSection
            isSubmitting={isSubmitting}
            onChange={(products) => {
              setFormState({
                ...formState,
                ecomVendorBundleProducts: products
              });
            }}
            selectedProducts={formState.ecomVendorBundleProducts}
            subTitle={
              <AppStack style={{ gap: 5 }}>
                <AppText style={{ fontSize: 14, color: '#666' }}>
                  Select the product to apply the volume discount to.
                </AppText>
                <AppText
                  style={{
                    fontSize: 14,
                    color: '#666'
                  }}
                >
                  To qualify, the customer must purchase the minimum quantity of
                  each individual product selected, not a combined total across
                  multiple products.
                </AppText>
              </AppStack>
            }
            title="Discounted products"
          />

          <EditProductCard
            contentProps={{ style: { padding: 0 } }}
            title="Discount options"
          >
            <AppStack style={{ gap: 0 }}>
              {formState.ecomVendorBundleDiscounts
                .sort((a, b) => a.sort - b.sort)
                .map((d, index) => (
                  <React.Fragment key={d.sort}>
                    <AppStack style={{ gap: 8, padding: 16 }}>
                      <BundleDiscountEditControls
                        bundleDiscount={d}
                        disabled={isSubmitting}
                        includeQuantitySelect
                        onChange={(updatedDiscount) =>
                          setFormState({
                            ...formState,
                            ecomVendorBundleDiscounts: formState.ecomVendorBundleDiscounts.map(
                              (bd) =>
                                bd.sort === d.sort
                                  ? { ...bd, ...updatedDiscount }
                                  : bd
                            )
                          })
                        }
                        onRemove={
                          formState.ecomVendorBundleDiscounts.length > 1
                            ? () => {
                                setFormState({
                                  ...formState,
                                  ecomVendorBundleDiscounts: formState.ecomVendorBundleDiscounts.filter(
                                    (bd) => bd.sort !== d.sort
                                  )
                                });
                              }
                            : null
                        }
                        title={`Option ${index + 1}`}
                      />
                    </AppStack>

                    <Divider />
                  </React.Fragment>
                ))}

              <AppFlexbox style={{ padding: 16, justifyContent: 'flex-end' }}>
                <Button
                  color="dark"
                  leftSection={<Plus size={16} />}
                  onClick={() => {
                    setFormState({
                      ...formState,
                      ecomVendorBundleDiscounts: [
                        ...formState.ecomVendorBundleDiscounts,
                        {
                          sort:
                            Math.max(
                              ...formState.ecomVendorBundleDiscounts.map(
                                (d) => d.sort
                              )
                            ) + 1,
                          minQuantity: 1,
                          fkEcomPricingType:
                            ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT,
                          pricingAmount: null
                        }
                      ]
                    });
                  }}
                  size="sm"
                  variant="outline"
                >
                  Add another option
                </Button>
              </AppFlexbox>
            </AppStack>
          </EditProductCard>

          <EditProductCard title="Bundle details">
            <AppStack>
              <TextInput
                disabled={isSubmitting}
                label="Title"
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    name: e.currentTarget.value
                  })
                }
                required
                value={formState.name}
              />
            </AppStack>
          </EditProductCard>
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 300 }}
        >
          <EditProductCard title="Status">
            <Select
              data={[
                { value: ECOM_PRODUCT_STATUS_ENUM.ACTIVE, label: 'Active' },
                { value: ECOM_PRODUCT_STATUS_ENUM.DRAFT, label: 'Draft' }
              ]}
              disabled={isSubmitting}
              onChange={(v) => {
                setFormState({
                  ...formState,
                  fkEcomProductStatus: v
                });
              }}
              value={formState.fkEcomProductStatus ?? ''}
            />
          </EditProductCard>

          <EditProductCard
            contentProps={{ style: { padding: 0 } }}
            title="Preview"
          >
            <AppStack style={{ marginTop: 4, gap: 0 }}>
              <Divider />
              <AppStack style={{ padding: 16, gap: 8 }}>
                {formState.ecomVendorBundleProducts.length === 0 ? (
                  <AppText
                    style={{
                      fontSize: 12,
                      fontStyle: 'italic',
                      color: '#666',
                      textAlign: 'center'
                    }}
                  >
                    Please select at least 1 product to see a preview of the
                    bundle discounts.
                  </AppText>
                ) : (
                  formState.ecomVendorBundleDiscounts
                    .sort((a, b) => a.minQuantity - b.minQuantity)
                    .map((d) => {
                      const previewSelected =
                        formState.selectedBundlePreview === d.sort;

                      return (
                        <AppCard
                          key={d.sort}
                          className={classes['hover-action-card-outlined']}
                          onClick={() =>
                            setFormState({
                              ...formState,
                              selectedBundlePreview:
                                formState.selectedBundlePreview === d.sort
                                  ? null
                                  : d.sort
                            })
                          }
                          style={
                            previewSelected
                              ? { outline: '2px solid dodgerblue' }
                              : null
                          }
                        >
                          <AppFlexbox
                            style={{
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <AppFlexbox style={{ gap: 8 }}>
                              <Checkbox checked={previewSelected} />
                              <AppText style={{ fontSize: 14 }}>
                                {`Buy ${d.minQuantity}${
                                  d.minQuantity === 1 ? ' item' : '+'
                                }`}
                              </AppText>
                            </AppFlexbox>

                            <AppText style={{ fontSize: 14, marginLeft: 8 }}>
                              $0.00
                            </AppText>
                          </AppFlexbox>
                        </AppCard>
                      );
                    })
                )}
              </AppStack>
            </AppStack>
          </EditProductCard>
        </AppStack>
      </AppFlexbox>

      {ConfirmDiscardModal}
    </PageView>
  );
};

VolumeDiscountBundleEdit.propTypes = {
  backPath: PropTypes.string,
  duplicate: PropTypes.bool,
  ecomVendorBundle: PropTypes.object,
  onBackClick: PropTypes.func
};

export default VolumeDiscountBundleEdit;
